// src/i18n/index.ts
// i18next configuration. Initializes the internationalization
// framework with English (default), French and Dutch locales.
//
// The active language is PERSISTED: every languageChanged event is
// written to localStorage, and a fresh boot restores the stored
// choice instead of resetting to English. Signed-in users
// additionally sync the preference to their account via
// src/lib/langPreference.ts (wired up in AuthContext).

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en.json";
import fr from "./fr.json";
import nl from "./nl.json";

export const supportedLanguages = ["en", "fr", "nl"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];
export const defaultLanguage: SupportedLanguage = "en";

/** localStorage key holding the visitor's language choice. */
export const LANGUAGE_STORAGE_KEY = "benchmark-lang";

/** The stored language choice, or null when absent/unsupported.
 *  Never throws: storage can be unavailable (private mode, SSR). */
export function readStoredLanguage(): SupportedLanguage | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return (supportedLanguages as readonly string[]).includes(value ?? "")
      ? (value as SupportedLanguage)
      : null;
  } catch {
    return null;
  }
}

function persistLanguage(language: string): void {
  // i18next can report region variants ("fr-FR"); store the base code.
  const base = language.toLowerCase().split(/[-_]/)[0] ?? "";
  if (!(supportedLanguages as readonly string[]).includes(base)) return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, base);
  } catch {
    // Storage unavailable: the session still works, persistence is lost.
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    nl: { translation: nl },
  },
  lng: readStoredLanguage() ?? defaultLanguage,
  fallbackLng: defaultLanguage,
  supportedLngs: [...supportedLanguages],
  interpolation: {
    escapeValue: false, // React already escapes
  },
  // Don't show keys when translations are missing - show the key as-is
  returnNull: false,
});

i18n.on("languageChanged", persistLanguage);

export default i18n;
